import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Search, X } from "lucide-react";
import { formatDkk, getGeckos, getProducts, getTerrariums } from "@/lib/site";

const TYPE_LABEL = { gecko: "Gekko", product: "Produkt", terrarium: "Terrarium" };

export default function SearchOverlay({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setQuery("");
    const t = setTimeout(() => inputRef.current?.focus(), 200);
    if (!items.length) {
      Promise.all([
        getGeckos().catch(() => []),
        getProducts().catch(() => []),
        getTerrariums().catch(() => []),
      ]).then(([geckos, products, terrariums]) =>
        setItems([
          ...geckos.map((x) => ({ ...x, _type: "gecko" })),
          ...products.map((x) => ({ ...x, _type: "product" })),
          ...terrariums
            .filter((x) => x.kind !== "custom")
            .map((x) => ({ ...x, _type: "terrarium" })),
        ])
      );
    }
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return items
      .filter((i) =>
        [i.name, i.category, i.base_color, i.pattern, i.description, ...(i.traits || [])]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      )
      .slice(0, 12);
  }, [items, query]);

  const suggestions = useMemo(() => {
    const geckos = items
      .filter((i) => i._type === "gecko" && i.status !== "Udsolgt" && (i.inventory_qty ?? 1) > 0)
      .slice(0, 3);
    const products = items.filter((i) => i._type === "product").slice(0, 2);
    return [...geckos, ...products];
  }, [items]);

  const go = (slug) => {
    onClose();
    navigate(`/vare/${slug}`);
    window.__lenis?.scrollTo(0, { immediate: true });
  };

  const renderRow = (i) => (
    <button
      key={i.slug}
      data-testid={`search-result-${i.slug}`}
      onClick={() => go(i.slug)}
      className="group flex w-full items-center gap-4 border-b border-white/5 px-2 py-3 text-left transition-colors duration-200 hover:bg-white/5"
    >
      <img
        src={i.image}
        alt={i.name}
        className="h-14 w-14 shrink-0 border border-white/10 object-cover"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-cream">{i.name}</span>
        <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-sage">
          {TYPE_LABEL[i._type]}
          {i.category ? ` · ${i.category}` : ""}
          {i._type === "gecko" && i.status !== "Tilgængelig" ? ` · ${i.status}` : ""}
        </span>
      </span>
      <span className="shrink-0 font-mono text-xs text-pale">
        {i.display_price || formatDkk(i.price_dkk)}
      </span>
      <ArrowUpRight className="h-4 w-4 shrink-0 text-sage transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-pale" />
    </button>
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-xl"
          onClick={onClose}
        >
          <div
            className="mx-auto max-w-3xl px-6 pt-24 sm:pt-32"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-4 border-b border-white/15 pb-4">
              <Search className="h-6 w-6 shrink-0 text-forest" />
              <input
                ref={inputRef}
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Søg efter gekkoer, foder, terrarier …"
                className="w-full bg-transparent font-serif text-2xl tracking-tight text-cream outline-none placeholder:text-sage/40 sm:text-3xl"
              />
              <button
                data-testid="search-close"
                onClick={onClose}
                aria-label="Luk søgning"
                className="shrink-0 text-sage transition-colors hover:text-cream"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mt-4 max-h-[55vh] overflow-y-auto pb-8">
              {query.trim() === "" && suggestions.length > 0 && (
                <>
                  <p className="px-2 pb-2 pt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-sage/70">
                    Populært lige nu
                  </p>
                  {suggestions.map(renderRow)}
                </>
              )}
              {query.trim() !== "" && results.length === 0 && (
                <p data-testid="search-empty" className="px-2 pt-8 text-center text-sm text-sage">
                  Ingen resultater for “{query.trim()}” — prøv fx “lillywhite” eller “jord”.
                </p>
              )}
              {results.map(renderRow)}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
